import { useEffect, useState } from "react";

import { Loader, Sparkles } from "lucide-react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getPersonaTrends } from "../api/persona";
import { usePersonaReportData } from "../context/PersonaReportDataContext";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const PERSONA_COLORS = [
  "#2E5BFF",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

const ALL_PERSONAS = "all";

const PersonaTrends = () => {
  const store = usePersonaReportData();
  const [activePersonaIdx, setActivePersonaIdx] = useState(ALL_PERSONAS);

  // When rendered inside the report the store supplies trends (fetched once on
  // entry); the standalone /persona/trends view has no provider, so self-fetch.
  const [local, setLocal] = useState({ data: null, loading: true });

  useEffect(() => {
    if (store) return undefined;
    const controller = new AbortController();
    getPersonaTrends({ signal: controller.signal })
      .then((r) => setLocal({ data: r.data, loading: false }))
      .catch(() => {
        if (!controller.signal.aborted) setLocal({ data: null, loading: false });
      });
    return () => controller.abort();
  }, [store]);

  const trends = store ? store.trends.data : local.data;
  const loading = store ? store.trends.loading : local.loading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 gap-3">
        <Loader className="w-6 h-6 animate-spin text-primary" />
        <span className="text-on-surface-variant text-sm">Loading trends…</span>
      </div>
    );
  }

  const personas = trends?.trends ?? [];

  if (!personas.length) {
    return (
      <div className="bg-surface-container-low rounded-2xl p-10 border border-surface-container-high text-center text-on-surface-variant text-sm">
        No trend data yet — generate reports over time to see trends.
      </div>
    );
  }

  const isAll = activePersonaIdx === ALL_PERSONAS;

  // Build merged chart data for the "all personas" view
  const allDatesMap = new Map();
  personas.forEach((p) => {
    (p.series ?? []).forEach((pt) => {
      const formatted = formatDate(pt.date);
      if (!allDatesMap.has(formatted)) allDatesMap.set(formatted, pt.date);
    });
  });
  const sortedDates = [...allDatesMap.entries()]
    .sort(([, aRaw], [, bRaw]) => new Date(aRaw) - new Date(bRaw))
    .map(([formatted]) => formatted);
  const allChartData = sortedDates.map((date) => {
    const point = { date };
    personas.forEach((p, idx) => {
      const match = (p.series ?? []).find((pt) => formatDate(pt.date) === date);
      if (match) point[`persona_${idx}`] = match.composite_score;
    });
    return point;
  });

  // Single-persona chart data
  const activePersona = isAll ? null : personas[activePersonaIdx];
  const chartData = isAll
    ? []
    : (activePersona?.series ?? []).map((pt) => ({
        date: formatDate(pt.date),
        score: pt.composite_score,
        rawDate: pt.date,
      }));

  // A ReferenceLine on a categorical axis only renders when `x` exactly matches
  // an existing tick. Fix dates rarely land on the same month as a scan, so snap
  // each marker to the nearest data point's category. Dedupe markers that snap to
  // the same category to avoid stacked overlapping labels.
  const fixMarkerCategories = isAll
    ? []
    : [
        ...new Set(
          (activePersona?.fix_markers ?? [])
            .map((fm) => {
              if (!chartData.length) return null;
              const target = new Date(fm.date).getTime();
              const nearest = chartData.reduce((best, pt) =>
                Math.abs(new Date(pt.rawDate).getTime() - target) <
                Math.abs(new Date(best.rawDate).getTime() - target)
                  ? pt
                  : best,
              );
              return nearest.date;
            })
            .filter(Boolean),
        ),
      ];

  const sharedAxisProps = {
    tick: { fill: "var(--color-on-surface-variant, #9aa0a6)", fontSize: 11 },
    tickLine: false,
    axisLine: false,
  };
  const sharedTooltipProps = {
    contentStyle: {
      background: "var(--color-surface-container-lowest, #1a1a2e)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 12,
      fontSize: 12,
    },
    labelStyle: { color: "var(--color-on-surface-variant, #9aa0a6)" },
  };

  return (
    <div className="space-y-6">
      <h2 className="font-headline text-xl font-bold">Score Trends</h2>

      {/* Persona tab selector */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActivePersonaIdx(ALL_PERSONAS)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
            isAll
              ? "bg-primary/10 border-primary text-primary"
              : "bg-surface-container-lowest border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
          }`}
        >
          All Personas
        </button>
        {personas.map((p, idx) => (
          <button
            key={p.persona_id ?? idx}
            onClick={() => setActivePersonaIdx(idx)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
              activePersonaIdx === idx
                ? "bg-primary/10 border-primary text-primary"
                : "bg-surface-container-lowest border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
            }`}
          >
            <span>{p.emoji}</span>
            <span>{p.persona_label}</span>
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-surface-container-low rounded-2xl p-6 border border-surface-container-high">
        <h3 className="font-headline text-base font-bold mb-4">
          {isAll
            ? "All Personas — Composite Score Over Time"
            : `${activePersona?.emoji} ${activePersona?.persona_label} — Composite Score Over Time`}
        </h3>

        {isAll ? (
          allChartData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-on-surface-variant text-sm">
              No data points yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={allChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" {...sharedAxisProps} />
                <YAxis domain={[0, 100]} {...sharedAxisProps} width={32} />
                <Tooltip {...sharedTooltipProps} />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "var(--color-on-surface-variant, #9aa0a6)" }}
                />
                {personas.map((p, idx) => (
                  <Line
                    key={p.persona_id ?? idx}
                    type="monotone"
                    dataKey={`persona_${idx}`}
                    name={`${p.emoji} ${p.persona_label}`}
                    stroke={PERSONA_COLORS[idx % PERSONA_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3, fill: PERSONA_COLORS[idx % PERSONA_COLORS.length] }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-on-surface-variant text-sm">
            No data points yet for this persona.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" {...sharedAxisProps} />
              <YAxis domain={[0, 100]} {...sharedAxisProps} width={32} />
              <Tooltip {...sharedTooltipProps} />
              <Legend
                wrapperStyle={{ fontSize: 12, color: "var(--color-on-surface-variant, #9aa0a6)" }}
              />
              {fixMarkerCategories.map((category) => (
                <ReferenceLine
                  key={category}
                  x={category}
                  stroke="#f59e0b"
                  strokeDasharray="4 4"
                  label={{
                    value: "Fix applied",
                    position: "top",
                    fontSize: 10,
                    fill: "#f59e0b",
                  }}
                />
              ))}
              <Line
                type="monotone"
                dataKey="score"
                name="Composite Score"
                stroke={PERSONA_COLORS[activePersonaIdx % PERSONA_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 4, fill: PERSONA_COLORS[activePersonaIdx % PERSONA_COLORS.length] }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Trend insight(s) — a short natural-language read on where scores are heading */}
      {isAll
        ? personas.some((p) => p.insight) && (
            <div className="bg-surface-container-low rounded-2xl p-6 border border-surface-container-high space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <h3 className="font-headline text-base font-bold">Trend Insights</h3>
              </div>
              <ul className="space-y-2">
                {personas
                  .filter((p) => p.insight)
                  .map((p, idx) => (
                    <li
                      key={p.persona_id ?? idx}
                      className="flex gap-2 text-sm text-on-surface-variant"
                    >
                      <span className="shrink-0">{p.emoji}</span>
                      <span>
                        <span className="font-medium text-on-surface">{p.persona_label}:</span>{" "}
                        {p.insight}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          )
        : activePersona?.insight && (
            <div className="bg-surface-container-low rounded-2xl p-5 border border-surface-container-high flex gap-3">
              <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-1">
                  Trend insight
                </p>
                <p className="text-sm text-on-surface">{activePersona.insight}</p>
              </div>
            </div>
          )}
    </div>
  );
};

export default PersonaTrends;
